import type { ReactNode } from 'react';
import { ProgressRing } from '../ui/ProgressRing';
import { formatCountdown } from '../../utils/formatters';

export interface BuildingUpgradeProgress {
    /** 0..1, `1 - remaining/total`. Fed straight to `ProgressRing`. */
    value: number;
    /** Milliseconds left until this build completes. */
    remainingMs: number;
}

export const RING_RADIUS = 27;

export const RING_STROKE_WIDTH = 4;

const CLUSTER_CLASSES = 'absolute -top-2 -right-2 flex h-[68px] w-[68px] items-center justify-center';

const RING_CLASSES = 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';

const COUNTDOWN_PILL_CLASSES = 'text-label absolute top-full mt-1 rounded-full bg-space-950/85 px-1.5 py-0.5 whitespace-nowrap';

export interface TileProgressClusterProps {
    /** Present while a build is in progress; omit for a settled tile. */
    upgrade?: BuildingUpgradeProgress;
    /** Rendered at the centre of the ring — `BuildingTile`'s level badge. A plot has no level, so it passes nothing. */
    children?: ReactNode;
}

/**
 * The ring + countdown a surface tile shows while a build runs, shared by
 * `BuildingTile` (an upgrade on a built position) and `PlotTile` (a
 * construction on an empty one) so both read identically — a construction in
 * progress is as visible as an upgrade.
 */
export function TileProgressCluster({ upgrade, children }: TileProgressClusterProps) {
    return (
        <div className={CLUSTER_CLASSES}>
            {upgrade && (
                <ProgressRing value={upgrade.value} radius={RING_RADIUS} strokeWidth={RING_STROKE_WIDTH} className={RING_CLASSES} />
            )}
            {children}
            {upgrade && <span className={COUNTDOWN_PILL_CLASSES}>{formatCountdown(upgrade.remainingMs)}</span>}
        </div>
    );
}

export default TileProgressCluster;
